import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls the window to the top on every route change.
 *
 * Why: React Router does NOT reset the scroll position by default.
 * If a user clicks a footer link while scrolled to the bottom of one page,
 * the next page would render with the browser still scrolled to the bottom —
 * making it look like the page "loaded from the bottom".
 *
 * Mount this component once, anywhere inside <BrowserRouter>, and every
 * client-side navigation (push / replace) will smoothly jump to the top.
 *
 * Edge cases handled:
 *  - hash links (e.g. /pricing#faq) keep their browser-native anchor scroll.
 *  - `state.preserveScroll` (passed from <Link state={{preserveScroll: true}}>)
 *    opts out for special cases like pagination on a single page.
 */
export default function ScrollToTop() {
  const { pathname, hash, state } = useLocation();

  useEffect(() => {
    if (state && state.preserveScroll) return;
    if (hash) {
      // Let the browser handle anchor scroll for in-page hash links
      const id = hash.replace(/^#/, '');
      const el = id ? document.getElementById(id) : null;
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
    }
    // Instant jump — prevents the "page slides up from bottom" feel.
    // Using auto (not smooth) keeps the route-change snappy.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash, state]);

  return null;
}
